import { useMemo } from "react";

import { useAuthContext } from "../../../lib/context/Auth/AuthContext";

import { transport } from "../grpcTransport";

import {
  GetActiveRoomsAndDesksRequest,
  HistoryEventsRequest,
  TimeRangeEventsRequest,
} from "./insights";

import { InsightsClient } from "./insights.client";

export type TimeRangeRequest = Omit<TimeRangeEventsRequest, "customerID">;
export type ActiveRoomsAndDesksRequest = Omit<
  GetActiveRoomsAndDesksRequest,
  "customerID"
>;

export const useInsightsRequests = () => {
  const { user, tokenInterceptor } = useAuthContext();

  const insightsClient = useMemo(() => new InsightsClient(transport), []);

  const buildOptions = (options?: { abort?: AbortSignal }) => ({
    interceptors: [tokenInterceptor],
    abort: options?.abort,
  });

  const getGeneralRoomStats = async (
    { locationID, startDate, endDate, timeZone }: TimeRangeRequest,
    options?: { abort?: AbortSignal }
  ) => {
    if (!user) {
      throw new Error("User not found!");
    }

    return insightsClient.roomGeneralStats(
      {
        customerID: user.customerid,
        locationID: locationID,
        startDate: startDate,
        endDate: endDate,
        timeZone,
      },
      buildOptions(options)
    );
  };

  const getAllRoomsOfLocation = async (
    { locationID, startDate, endDate, timeZone }: TimeRangeRequest,
    options?: { abort?: AbortSignal }
  ) => {
    if (!user) {
      throw new Error("User not found!");
    }

    return insightsClient.generalStatsByRooms(
      {
        customerID: user.customerid,
        locationID: locationID,
        startDate: startDate,
        endDate: endDate,
        timeZone,
      },
      buildOptions(options)
    );
  };

  const getRoomsTimeRangeEvents = async (
    { locationID, startDate, endDate, timeZone }: TimeRangeRequest,
    options?: { abort?: AbortSignal }
  ) => {
    if (!user) {
      throw new Error("User not found!");
    }

    return insightsClient.roomTimeRangeEvents(
      {
        customerID: user.customerid,
        locationID: locationID,
        startDate: startDate,
        endDate: endDate,
        timeZone,
      },
      buildOptions(options)
    );
  };

  const getTotalOccupancy = async (
    { locationID, startDate, endDate, timeZone }: TimeRangeRequest,
    options?: { abort?: AbortSignal }
  ) => {
    if (!user) {
      throw new Error("User not found!");
    }

    return insightsClient.totalOccupancy(
      {
        customerID: user.customerid,
        locationID: locationID,
        startDate: startDate,
        endDate: endDate,
        timeZone,
      },
      buildOptions(options)
    );
  };

  const getRoomHeatMapEvents = async (
    { locationID, startDate, endDate, timeZone }: TimeRangeRequest,
    options?: { abort?: AbortSignal }
  ) => {
    if (!user) {
      throw new Error("User not found!");
    }

    return insightsClient.roomEventsHeatMap(
      {
        customerID: user.customerid,
        locationID: locationID,
        startDate: startDate,
        endDate: endDate,
        timeZone,
      },
      buildOptions(options)
    );
  };

  const getRoomBehavior = async (
    { locationID, startDate, endDate, timeZone }: TimeRangeRequest,
    options?: { abort?: AbortSignal }
  ) => {
    if (!user) {
      throw new Error("User not found!");
    }

    return insightsClient.roomMeetingBehavior(
      {
        customerID: user.customerid,
        locationID: locationID,
        startDate: startDate,
        endDate: endDate,
        timeZone,
      },
      buildOptions(options)
    );
  };

  const getRoomReservationFlor = async (
    { locationID, startDate, endDate, timeZone }: TimeRangeRequest,
    options?: { abort?: AbortSignal }
  ) => {
    if (!user) {
      throw new Error("User not found!");
    }

    return insightsClient.roomSankeyChart(
      {
        customerID: user.customerid,
        locationID: locationID,
        startDate: startDate,
        endDate: endDate,
        timeZone,
      },
      buildOptions(options)
    );
  };

  const getRoomReservationsHistory = async (
    {
      locationID,
      startDate,
      endDate,
      timeZone,
      limit,
      offset,
      organizers,
    }: Omit<HistoryEventsRequest, "customerID">,
    options?: { abort?: AbortSignal }
  ) => {
    if (!user) {
      throw new Error("User not found!");
    }

    return insightsClient.roomHistoryEvents(
      {
        customerID: user.customerid,
        locationID,
        startDate,
        endDate,
        timeZone,
        limit,
        offset,
        organizers,
      },
      buildOptions(options)
    );
  };

  const exportRoomInsights = async (
    { locationID, startDate, endDate, timeZone }: TimeRangeRequest,
    options?: { abort?: AbortSignal }
  ) => {
    if (!user) {
      throw new Error("User not found!");
    }

    return insightsClient.roomInsightsExport(
      {
        customerID: user.customerid,
        locationID: locationID,
        startDate: startDate,
        endDate: endDate,
        timeZone,
      },
      buildOptions(options)
    );
  };

  const exportRoomHistoryInsights = async (
    {
      locationID,
      startDate,
      endDate,
      timeZone,
      limit,
      offset,
      organizers,
    }: Omit<HistoryEventsRequest, "customerID">,
    options?: { abort?: AbortSignal }
  ) => {
    if (!user) {
      throw new Error("User not found!");
    }

    return insightsClient.roomHistoryEventsExport(
      {
        customerID: user.customerid,
        locationID: locationID,
        startDate: startDate,
        endDate: endDate,
        timeZone,
        limit,
        offset,
        organizers,
      },
      buildOptions(options)
    );
  };

  const getActiveRoomsAndDesks = async (
    { locationID }: ActiveRoomsAndDesksRequest,
    options?: { abort?: AbortSignal }
  ) => {
    if (!user) {
      throw new Error("User not found!");
    }

    return insightsClient.getActiveRoomsAndDesks(
      {
        customerID: user.customerid,
        locationID: locationID,
      },
      buildOptions(options)
    );
  };

  return {
    getGeneralRoomStats,
    getAllRoomsOfLocation,
    getRoomsTimeRangeEvents,
    getTotalOccupancy,
    getRoomHeatMapEvents,
    getRoomBehavior,
    getRoomReservationFlor,
    exportRoomInsights,
    getRoomReservationsHistory,
    exportRoomHistoryInsights,
    getActiveRoomsAndDesks,
  };
};
